import { useState } from 'react';

import { ApiError } from '../api/client';
import { useAuth } from '../auth/useAuth';

/**
 * The only screen an unauthenticated visitor can reach.
 *
 * The error text is whatever the API said, because the API deliberately says the
 * same thing for a wrong username as for a wrong password — guessing which half
 * was right should not be possible from here either.
 */
export function LoginPage() {
  const { signIn } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await signIn(username, password);
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.message
          ? extractMessage(cause.message)
          : 'Sign-in failed. Check the API is running.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <div className="login__brand">
          <div className="nav__mark">H</div>
          <div>
            <div className="nav__title">Price Radar</div>
            <div className="nav__subtitle">Heureka.sk</div>
          </div>
        </div>

        <p className="login__lede">Sign in to see your price position.</p>

        <div className="field">
          <label className="field__label" htmlFor="username">
            Username
          </label>
          <input
            id="username"
            className="control"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className="control"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        {error && (
          <div className="login__error" role="alert">
            {error}
          </div>
        )}

        <button type="submit" className="button button--primary" disabled={busy || !username || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

/** Nest wraps its errors in JSON; show the sentence, not the envelope. */
function extractMessage(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as { message?: string | string[] };

    if (Array.isArray(parsed.message)) {
      return parsed.message.join(', ');
    }

    return parsed.message ?? raw;
  } catch {
    return raw;
  }
}
